import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  getDocs,
  addDoc,
  serverTimestamp,
  enableIndexedDbPersistence
} from 'firebase/firestore';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
const auth = getAuth(app);
const db = getFirestore(app);

// Set auth persistence to LOCAL (persists even when browser is closed)
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Auth persistence error:', error);
});

// Enable offline persistence for Firestore
enableIndexedDbPersistence(db).catch((err) => {
  if (err.code === 'failed-precondition') {
    console.warn('Firestore persistence failed: Multiple tabs open');
  } else if (err.code === 'unimplemented') {
    console.warn('Firestore persistence not available in this browser');
  }
});

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// ==================== AUTH FUNCTIONS ====================

/**
 * Sign in with Google
 */
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Create or update user document in Firestore
    await setDoc(
      doc(db, 'users', user.uid),
      {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        lastLogin: serverTimestamp(),
      },
      { merge: true }
    );

    return user;
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

/**
 * Sign up with Email and Password
 */
export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;

    await updateProfile(user, {
      displayName: displayName,
    });

    await setDoc(
      doc(db, 'users', user.uid),
      {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        photoURL: null,
        lastLogin: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    return user;
  } catch (error) {
    console.error('Email sign-up error:', error);
    throw error;
  }
};

/**
 * Sign in with Email and Password
 */
export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;

    await setDoc(
      doc(db, 'users', user.uid),
      {
        lastLogin: serverTimestamp(),
      },
      { merge: true }
    );

    return user;
  } catch (error) {
    console.error('Email sign-in error:', error);
    throw error;
  }
};

/**
 * Sign out current user
 */
export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
};

/**
 * Auth state observer
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// ==================== USER DATA FUNCTIONS ====================

/**
 * Get user profile data
 */
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    return userDoc.exists() ? userDoc.data() : null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Update user preferences
 */
export const updateUserPreferences = async (userId, preferences) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      preferences,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating preferences:', error);
    throw error;
  }
};

// ==================== QUIZ & PROGRESS FUNCTIONS ====================

/**
 * Save quiz result to Firestore with review data
 */
export const saveQuizResult = async (userId, quizData) => {
  try {
    const quizRef = doc(collection(db, 'users', userId, 'quizzes'));
    await setDoc(quizRef, {
      ...quizData,
      timestamp: serverTimestamp(),
      reviewed: false, // Track if user has reviewed this quiz
    });
    return quizRef.id;
  } catch (error) {
    console.error('Error saving quiz result:', error);
    throw error;
  }
};

/**
 * Mark quiz as reviewed
 */
export const markQuizAsReviewed = async (userId, quizId) => {
  try {
    const quizRef = doc(db, 'users', userId, 'quizzes', quizId);
    await updateDoc(quizRef, {
      reviewed: true,
      reviewedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error marking quiz as reviewed:', error);
    throw error;
  }
};

/**
 * Get single quiz by ID for review
 */
export const getQuizById = async (userId, quizId) => {
  try {
    const quizRef = doc(db, 'users', userId, 'quizzes', quizId);
    const quizDoc = await getDoc(quizRef);
    
    if (quizDoc.exists()) {
      return {
        id: quizDoc.id,
        ...quizDoc.data(),
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching quiz:', error);
    throw error;
  }
};

/**
 * Get user's quiz history
 */
export const getQuizHistory = async (userId, limitCount = 20) => {
  try {
    const quizzesRef = collection(db, 'users', userId, 'quizzes');
    const q = query(quizzesRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching quiz history:', error);
    throw error;
  }
};

/**
 * Save learning session data
 */
export const saveLearningSession = async (userId, sessionData) => {
  try {
    const sessionsRef = collection(db, 'users', userId, 'sessions');
    await addDoc(sessionsRef, {
      ...sessionData,
      timestamp: serverTimestamp(),
    });
    console.log('Learning session saved successfully');
  } catch (error) {
    console.error('Error saving learning session:', error);
    throw error;
  }
};

/**
 * Get user's learning history
 */
export const getLearningHistory = async (userId, limitCount = 20) => {
  try {
    const sessionsRef = collection(db, 'users', userId, 'sessions');
    const q = query(sessionsRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching learning history:', error);
    throw error;
  }
};

/**
 * Get user's favorite topics based on repeated learning
 */
export const getFavoriteTopics = async (userId) => {
  try {
    const sessionsRef = collection(db, 'users', userId, 'sessions');
    const q = query(sessionsRef, where('type', '==', 'explanation'), limit(50));
    const querySnapshot = await getDocs(q);
    
    const topicCount = {};
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const topic = data.topic || 'Unknown';
      topicCount[topic] = (topicCount[topic] || 0) + 1;
    });
    
    return Object.entries(topicCount)
      .map(([topic, count]) => ({ topic, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  } catch (error) {
    console.error('Error fetching favorite topics:', error);
    throw error;
  }
};

/**
 * Get user's weak areas based on quiz performance
 */
export const getWeakAreas = async (userId) => {
  try {
    const quizzesRef = collection(db, 'users', userId, 'quizzes');
    const q = query(
      quizzesRef, 
      where('score', '<', 60), 
      orderBy('score', 'asc'),
      limit(10)
    );
    const querySnapshot = await getDocs(q);
    
    const weakTopics = {};
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const topic = data.topic || 'General';
      weakTopics[topic] = (weakTopics[topic] || 0) + 1;
    });
    
    return Object.entries(weakTopics)
      .map(([topic, count]) => ({ topic, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error fetching weak areas:', error);
    throw error;
  }
};

/**
 * Update user's learning progress
 */
export const updateProgress = async (userId, progressData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      progress: progressData,
      lastUpdated: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating progress:', error);
    throw error;
  }
};

// Export Firebase instances
export { app, auth, db, googleProvider };